import React from 'react';

/**
 * Numeric input with up/down stepper buttons attached to the right side.
 * Reusable wrapper — pass it value, onChange, step, min, max, and any other props.
 *
 *   <NumberStepper value={x} onChange={setX} step={1} min={0} />
 *
 * Renders as: [    25       │ ▲ │
 *                            │ ▼ │
 */
export default function NumberStepper({
  value,
  onChange,
  step = 1,
  min = 0,
  max,
  placeholder,
  inputClassName = '',
  inputStyle = {},
  ...inputProps
}) {
  const numeric = parseFloat(value);
  const safeNum = Number.isFinite(numeric) ? numeric : 0;

  const tick = (delta) => {
    const next = Math.max(
      min !== undefined ? min : -Infinity,
      Math.min(max !== undefined ? max : Infinity, safeNum + delta)
    );
    // Round to step precision for clean numbers (avoid 2.0000000001)
    const stepStr = String(step);
    const decimals = stepStr.includes('.') ? stepStr.split('.')[1].length : 0;
    onChange(decimals > 0 ? next.toFixed(decimals) : String(Math.round(next)));
  };

  const btnStyle = {
    width: '1.6rem',
    height: '50%',
    background: '#fefdf8',
    border: 'none',
    color: '#1a1a2e',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.86rem',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    padding: 0,
    transition: 'background 0.1s',
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        position: 'relative',
        ...inputStyle,
      }}
    >
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        step={step}
        min={min}
        max={max}
        placeholder={placeholder}
        className={inputClassName}
        style={{
          flex: 1,
          minWidth: 0,
          paddingRight: '2rem',
          marginTop: 0,
        }}
        {...inputProps}
      />
      <div
        style={{
          position: 'absolute',
          right: '2px',
          top: '2px',
          bottom: '2px',
          width: '1.6rem',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1.5px solid rgba(26, 26, 46, 0.12)',
          overflow: 'hidden',
          borderRadius: '0 0.45rem 0.45rem 0',
        }}
      >
        <button
          type="button"
          onClick={() => tick(step)}
          aria-label={`Increase by ${step}`}
          style={{
            ...btnStyle,
            borderBottom: '1px solid rgba(26, 26, 46, 0.08)',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#faf5ec'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#fefdf8'}
        >
          ▲
        </button>
        <button
          type="button"
          onClick={() => tick(-step)}
          disabled={min !== undefined && safeNum <= min}
          aria-label={`Decrease by ${step}`}
          style={{
            ...btnStyle,
            opacity: (min !== undefined && safeNum <= min) ? 0.35 : 1,
            cursor: (min !== undefined && safeNum <= min) ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) e.currentTarget.style.background = '#faf5ec';
          }}
          onMouseLeave={(e) => e.currentTarget.style.background = '#fefdf8'}
        >
          ▼
        </button>
      </div>
    </div>
  );
}
